import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Button,
  Typography,
  TextField,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import StatusCard from "../Card/StatusCard";
import api from "../../api/axios";
import { useCan } from "../../hooks/useCan";
import { STATUS_OPTIONS_PRIVATE } from "../../constants/status";
import { generateDashboardAiReportPdf } from "../../pdfs/generateDashboardAiReportPdf";

export default function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState([]);
  const canCreateAgreement = useCan("Agreement", "create");
  const canReadAgreement = useCan("Agreement", "read");

  const [loadingReport, setLoadingReport] = useState(false);
  const [report, setReport] = useState("");
  const [openReport, setOpenReport] = useState(false);
  const [userPrompt, setUserPrompt] = useState("");

  const groupedSections = {
    "Información General": ["all"],
    "Fase Inicial": ["draft", "process"],
    "Revisión Rectoría": [
      "sent_to_rector",
      "approved_by_rector",
      "rejected_by_rector",
    ],
    "Revisión Solicitante": [
      "sent_to_applicant",
      "approved_by_applicant",
      "rejected_by_applicant",
    ],
    "Firma y Cierre": [
      "wait_to_sign",
      "signed",
      "next_to_expired",
      "expired",
      "finished",
    ],
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      const response = await api.get("/api/v1/dashboard", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
        },
      });
      setData(response.data);
    } catch (err) {
      console.error("Error al cargar el dashboard:", err);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleAddAgreement = () => {
    navigate("/agreement/new");
  };

  const handleGenerateReport = async () => {
    setLoadingReport(true);
    try {
      const response = await api.post("/api/v1/ai_dashboard_report", {
        data,
        prompt: userPrompt, // opcional
      });
      setReport(response.data.report);
      // abrimos el modal de visualización del reporte generado
      setOpenReport(false);
      setTimeout(() => {
        setOpenResult(true);
      }, 100);
    } catch (err) {
      console.error("Error generando informe IA:", err);
    } finally {
      setLoadingReport(false);
    }
  };

  // Estado para mostrar el resultado del informe
  const [openResult, setOpenResult] = useState(false);

  return (
    <Box sx={{ p: isSmallScreen ? 2 : 4 }}>
      {/* Header */}
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h5" fontWeight="bold">
          Gestión de Convenios
        </Typography>
        <Box>
          {canCreateAgreement && (
            <Button
              variant="contained"
              sx={{ mr: 2 }}
              onClick={handleAddAgreement}
            >
              Agregar Convenio
            </Button>
          )}
          <Button variant="outlined" onClick={() => setOpenReport(true)}>
            Generar Informe IA
          </Button>
        </Box>
      </Grid>

      {/* Secciones */}
      {canReadAgreement && (
        <Grid container spacing={3}>
          {Object.entries(groupedSections).map(([sectionTitle, statuses]) => {
            const sectionItems = data.filter((item) =>
              statuses.includes(item.status)
            );
            if (!sectionItems.length) return null;

            return (
              <Grid item xs={12} md={6} lg={4} key={sectionTitle}>
                <Box
                  sx={{
                    borderRadius: 3,
                    backgroundColor: "#fafafa",
                    p: 2,
                    boxShadow: 1,
                    height: "100%",
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    color={theme.palette.primary.main}
                    gutterBottom
                  >
                    {sectionTitle}
                  </Typography>
                  <Grid container spacing={2}>
                    {sectionItems.map((status) => {
                      const cfg = STATUS_OPTIONS_PRIVATE.find(
                        (s) => s.value === status.status
                      );
                      return (
                        <Grid item xs={12} sm={6} key={status.status}>
                          <StatusCard
                            quantity={status.quantity}
                            name={cfg?.label || status.name}
                            color={cfg?.color}
                            status={status.status}
                            icon={cfg?.icon}
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Dialog para ingresar prompt opcional */}
      <Dialog
        open={openReport}
        onClose={() => setOpenReport(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Generar Informe con IA</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" gutterBottom>
            Si no escribes nada, se usará un prompt por defecto.
          </Typography>
          <TextField
            fullWidth
            label="Prompt personalizado (opcional)"
            placeholder="Escribe tu prompt aquí"
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            multiline
            minRows={4}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenReport(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleGenerateReport}
            disabled={loadingReport}
          >
            {loadingReport ? "Generando..." : "Generar Informe"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para mostrar el resultado */}
      <Dialog
        open={openResult}
        onClose={() => setOpenResult(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Informe generado con IA</DialogTitle>
        <DialogContent dividers>
          {report ? (
            report.split("\n").map((line, index) => {
              if (line.startsWith("## ")) {
                return (
                  <Typography
                    key={index}
                    variant="h6"
                    fontWeight="bold"
                    gutterBottom
                  >
                    {line.replace("## ", "")}
                  </Typography>
                );
              }
              const parts = line.split(/(\*\*.*?\*\*)/g);
              return (
                <Typography key={index} variant="body1" paragraph>
                  {parts.map((part, i) => {
                    if (part.startsWith("**") && part.endsWith("**")) {
                      return <strong key={i}>{part.slice(2, -2)}</strong>;
                    }
                    return part;
                  })}
                </Typography>
              );
            })
          ) : (
            <Typography variant="body1">No hay informe disponible</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenResult(false)}>Cerrar</Button>
          <Button
            variant="contained"
            onClick={() => generateDashboardAiReportPdf(report)}
          >
            Exportar PDF
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
